import {
  AlertOctagon,
  AlertTriangle,
  Bell,
  Clock,
  Eye,
  Mail,
  MessageSquare,
  Monitor,
  UserPlus,
  Volume2,
  X,
} from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Button, Input } from 'common-src/components/base';

import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_KEY_BY_ID,
  NOTIFICATION_CATEGORY_LABEL_BY_ID,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_COLOR,
  NOTIFICATION_ICON_KEY,
  NOTIFICATION_STATUS,
} from '../../constants';
import { isTaskNotificationChannelEnabled } from '../../helpers';
import styles from './TaskNotifications.module.scss';

const channelIconByKey = {
  browser: Monitor,
  inApp: Bell,
  slack: MessageSquare,
  email: Mail,
};

const notificationIconByKey = {
  critical: AlertTriangle,
  assignments: UserPlus,
  escalations: AlertOctagon,
  followedChanges: Eye,
  overdue: Clock,
};

const getSlackOptionLabel = (option) => {
  if (!option) return '';
  return option?.slackLabel || option?.slackHandle || '';
};

const getRelativeNotificationTime = (value) => {
  if (!value) return '';

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return '';

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(value).toLocaleDateString();
};

const Switch = ({ checked, onClick, ariaLabel, disabled = false }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    aria-pressed={checked}
    disabled={disabled}
    className={`${styles.switch} ${checked ? styles.switchChecked : ''}`}
    onClick={onClick}
  >
    <span className={styles.switchHandle} />
  </button>
);

const TaskNotifications = ({
  preferences,
  currentUserEmail,
  isLoading,
  isSaving,
  onPatchPreferences,
  onSearchSlackUsers,
  onSendTestMessage,
  taskNotifications = [],
  isTaskNotificationsLoading,
  onDismissTaskNotification,
  onOpenTaskNotification,
  browserNotificationPermission,
  onRequestBrowserNotificationPermission,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftSlackUserId, setDraftSlackUserId] = useState('');
  const [selectedSlackUser, setSelectedSlackUser] = useState(null);
  const [slackSearchResults, setSlackSearchResults] = useState([]);
  const [isSlackSearchLoading, setIsSlackSearchLoading] = useState(false);
  const [isSlackSearchOpen, setIsSlackSearchOpen] = useState(false);
  const [draftEmailAddress, setDraftEmailAddress] = useState('');
  const notificationsRef = useRef(null);

  useEffect(() => {
    if (!preferences) return;

    const savedSlackUserId = preferences?.channels?.slack?.slackUserId || '';
    const savedSlackLabel = preferences?.channels?.slack?.slackLabel || '';

    if (savedSlackUserId && savedSlackLabel) {
      setDraftSlackUserId(savedSlackLabel);
      setSelectedSlackUser({
        slackUserId: savedSlackUserId,
        slackLabel: savedSlackLabel,
      });
    } else {
      setDraftSlackUserId(savedSlackUserId);
      setSelectedSlackUser(null);
    }

    setSlackSearchResults([]);
    setIsSlackSearchOpen(false);
    setDraftEmailAddress(preferences?.channels?.email?.emailAddress || currentUserEmail || '');
  }, [currentUserEmail, preferences]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const normalizedValue = `${draftSlackUserId || ''}`.trim();
    const searchQuery = normalizedValue;
    const selectedLabel = getSlackOptionLabel(selectedSlackUser);

    if (!onSearchSlackUsers || searchQuery.length < 2) {
      setSlackSearchResults([]);
      setIsSlackSearchLoading(false);
      setIsSlackSearchOpen(false);
      return undefined;
    }

    if (selectedSlackUser && normalizedValue === selectedLabel) {
      setIsSlackSearchOpen(false);
      return undefined;
    }

    let isCancelled = false;

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSlackSearchLoading(true);
        const results = await onSearchSlackUsers(searchQuery);
        if (isCancelled) return;

        setSlackSearchResults(results);
        setIsSlackSearchOpen(true);
      } catch {
        if (isCancelled) return;
        setSlackSearchResults([]);
      } finally {
        if (!isCancelled) {
          setIsSlackSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [draftSlackUserId, isOpen, onSearchSlackUsers, selectedSlackUser]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (notificationsRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const hasLoadedPreferences = !!preferences;
  const resolvedPreferences = preferences;
  const isDisabled = isSaving;
  const hasSelectedSlackUser =
    selectedSlackUser != null &&
    `${draftSlackUserId || ''}`.trim() === getSlackOptionLabel(selectedSlackUser);
  const isSlackApplyDisabled =
    isDisabled || (!!`${draftSlackUserId || ''}`.trim() && !hasSelectedSlackUser);
  const isEmailApplyDisabled = isDisabled;
  const visibleTaskNotifications =
    resolvedPreferences?.notificationsEnabled &&
    isTaskNotificationChannelEnabled(resolvedPreferences, 'inApp')
      ? taskNotifications.filter((notification) => {
          const categoryKey = NOTIFICATION_CATEGORY_KEY_BY_ID[notification?.category];
          if (!categoryKey) return false;

          return !!resolvedPreferences?.categories?.[categoryKey]?.inApp;
        })
      : [];

  const sentTaskNotificationsCount = visibleTaskNotifications.filter(
    (notification) => notification?.status === NOTIFICATION_STATUS.Sent,
  ).length;

  const taskNotificationsBadgeLabel =
    sentTaskNotificationsCount > 99 ? '99+' : String(sentTaskNotificationsCount);

  const handleToggleNotificationsEnabled = async () => {
    if (!resolvedPreferences) return;

    const nextValue = !resolvedPreferences.notificationsEnabled;

    await onPatchPreferences({ notificationsEnabled: nextValue }, (prev) => ({
      ...prev,
      notificationsEnabled: nextValue,
    }));
  };

  const handleToggleSound = async () => {
    if (!resolvedPreferences) return;

    const nextValue = !resolvedPreferences.sound;

    await onPatchPreferences({ sound: nextValue }, (prev) => ({
      ...prev,
      sound: nextValue,
    }));
  };

  const handleApplyChannelValue = async (channelKey, fieldKey, value) => {
    if (!resolvedPreferences) return;

    const normalizedValue = `${value || ''}`.trim();

    const didSavePreferences = await onPatchPreferences(
      {
        channels: {
          [channelKey]: {
            [fieldKey]: normalizedValue,
          },
        },
      },
      (prev) => ({
        ...prev,
        channels: {
          ...prev.channels,
          [channelKey]: {
            ...prev.channels[channelKey],
            [fieldKey]: normalizedValue,
          },
        },
      }),
    );

    if (!didSavePreferences) return;
    if (!normalizedValue) return;

    if (channelKey === 'email') {
      await onSendTestMessage?.({ emailAddress: normalizedValue });
      return;
    }

    if (channelKey === 'slack') {
      await onSendTestMessage?.({ slackUserId: normalizedValue });
    }
  };

  const handleSlackInputChange = (value) => {
    setDraftSlackUserId(value);

    const selectedLabel = getSlackOptionLabel(selectedSlackUser);
    if (selectedSlackUser && value.trim() !== selectedLabel) {
      setSelectedSlackUser(null);
    }
  };

  const handleSlackOptionSelect = (option) => {
    setSelectedSlackUser(option);
    setDraftSlackUserId(getSlackOptionLabel(option));
    setIsSlackSearchOpen(false);
  };

  const handleApplySlackValue = async () => {
    const normalizedValue = `${draftSlackUserId || ''}`.trim();

    if (!normalizedValue) {
      await handleApplyChannelValue('slack', 'slackUserId', '');
      return;
    }

    if (hasSelectedSlackUser) {
      await handleApplyChannelValue('slack', 'slackUserId', selectedSlackUser.slackUserId);
    }
  };

  const handleMatrixToggle = async (categoryKey, channelKey) => {
    if (!resolvedPreferences) return;

    const nextValue = !resolvedPreferences.categories[categoryKey][channelKey];
    const nextCategories = {
      ...resolvedPreferences.categories,
      [categoryKey]: {
        ...resolvedPreferences.categories[categoryKey],
        [channelKey]: nextValue,
      },
    };
    const nextChannelEnabled = Object.values(nextCategories).some(
      (categoryPreferences) => !!categoryPreferences?.[channelKey],
    );

    await onPatchPreferences(
      {
        categories: {
          [categoryKey]: {
            [channelKey]: nextValue,
          },
        },
        channels: {
          [channelKey]: {
            enabled: nextChannelEnabled,
          },
        },
      },
      (prev) => ({
        ...prev,
        channels: {
          ...prev.channels,
          [channelKey]: {
            ...prev.channels[channelKey],
            enabled: nextChannelEnabled,
          },
        },
        categories: {
          ...prev.categories,
          [categoryKey]: {
            ...prev.categories[categoryKey],
            [channelKey]: nextValue,
          },
        },
      }),
    );
  };

  const handleDismissNotification = async (event, notificationId) => {
    event.stopPropagation();

    const didDismiss = await onDismissTaskNotification?.(notificationId);
    return didDismiss;
  };

  const handleOpenNotification = async (notification) => {
    const didOpen = await onOpenTaskNotification?.(notification);

    if (didOpen) {
      setIsOpen(false);
    }
  };

  const handleBellClick = async () => {
    if (browserNotificationPermission === 'default') {
      await onRequestBrowserNotificationPermission?.();
    }

    setIsOpen((prev) => !prev);
  };

  const renderTaskNotificationsFeed = () => {
    if (isTaskNotificationsLoading) {
      return <div className={styles.emptyState}>Loading task notifications...</div>;
    }

    if (!visibleTaskNotifications.length) {
      return <div className={styles.emptyState}>No task notifications</div>;
    }

    return visibleTaskNotifications.map((notification) => {
      const iconKey = NOTIFICATION_ICON_KEY[notification.category];
      const Icon = notificationIconByKey[iconKey] || Bell;
      const categoryLabel =
        NOTIFICATION_CATEGORY_LABEL_BY_ID[notification.category] || 'Notification';
      const dotColor = NOTIFICATION_COLOR[notification.category] || '#d4d4d8';
      const isUnread = notification.status === NOTIFICATION_STATUS.Sent;

      return (
        <div
          key={notification.id}
          role="button"
          tabIndex={0}
          className={`${styles.notificationCard} ${isUnread ? styles.notificationCardUnread : ''}`}
          onClick={() => handleOpenNotification(notification)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpenNotification(notification);
            }
          }}
        >
          <div className={styles.notificationDot} style={{ backgroundColor: dotColor }} />

          <div className={styles.notificationContent}>
            <div className={styles.notificationHeaderRow}>
              <div className={styles.notificationTitleRow}>
                <Icon className={styles.notificationCategoryIcon} />
                <span className={styles.notificationTitle}>
                  {notification.subject || categoryLabel}
                </span>
              </div>

              <button
                type="button"
                className={styles.dismissButton}
                aria-label="Dismiss notification"
                onClick={(event) => handleDismissNotification(event, notification.id)}
              >
                <X className={styles.dismissIcon} />
              </button>
            </div>

            {!!notification.body && (
              <div className={styles.notificationSubtitle}>{notification.body}</div>
            )}

            <span className={styles.notificationTime}>
              {getRelativeNotificationTime(notification.updatedAt)}
            </span>
          </div>
        </div>
      );
    });
  };

  const renderSlackSearchResults = () => {
    if (!isSlackSearchOpen) return null;

    if (isSlackSearchLoading) {
      return <div className={styles.slackSearchState}>Searching Slack...</div>;
    }

    if (!slackSearchResults.length) {
      return <div className={styles.slackSearchState}>No matching Slack results</div>;
    }

    return (
      <div className={styles.slackSearchResults}>
        {slackSearchResults.map((option) => (
          <button
            key={`${option.workspace}-${option.slackUserId}`}
            type="button"
            className={styles.slackSearchOption}
            onClick={() => handleSlackOptionSelect(option)}
          >
            <span className={styles.slackSearchPrimary}>
              <span>{getSlackOptionLabel(option)}</span>
              <span className={styles.slackWorkspaceBadge}>{option.workspace}</span>
            </span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.root} ref={notificationsRef}>
      <button
        type="button"
        className={styles.bellButton}
        aria-label={`Open task notifications${sentTaskNotificationsCount ? `, ${taskNotificationsBadgeLabel} unread` : ''}`}
        aria-expanded={isOpen}
        onClick={handleBellClick}
      >
        <Bell className={styles.bellIcon} />
        {!!sentTaskNotificationsCount && (
          <span className={styles.notificationBadge}>{taskNotificationsBadgeLabel}</span>
        )}
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <span className={styles.title}>Task Notifications</span>
            <Switch
              checked={resolvedPreferences?.notificationsEnabled || false}
              onClick={handleToggleNotificationsEnabled}
              ariaLabel="Toggle task notifications"
              disabled={!hasLoadedPreferences || isDisabled}
            />
          </div>

          {isLoading && !hasLoadedPreferences && (
            <div className={styles.section}>
              <div className={styles.emptyState}>Loading notification preferences...</div>
            </div>
          )}

          {!!resolvedPreferences?.notificationsEnabled && (
            <>
              <div className={styles.section}>
                <div className={styles.channelRow}>
                  <span className={styles.fieldLabel}>Slack User</span>
                  <div className={styles.slackInputGroup}>
                    <Input
                      value={draftSlackUserId}
                      placeholder="Search Slack user or channel"
                      onTextChange={handleSlackInputChange}
                      onClick={() => {
                        if (slackSearchResults.length) {
                          setIsSlackSearchOpen(true);
                        }
                      }}
                      withError={false}
                      classNames={[styles.inputContainer]}
                      inputClassNames={[styles.inputWrapper]}
                    />
                    {renderSlackSearchResults()}
                  </div>
                  <Button
                    text="Apply"
                    onClick={handleApplySlackValue}
                    disabled={isSlackApplyDisabled}
                    backgroundColor="#FFFFFF"
                    borderColor="#E4E4E7"
                    textColor="#A1A1AA"
                    classNames={[styles.applyButton]}
                  />
                </div>

                <div className={styles.channelRow}>
                  <span className={styles.fieldLabel}>Email</span>
                  <Input
                    value={draftEmailAddress}
                    placeholder="you@example.com"
                    onTextChange={setDraftEmailAddress}
                    withError={false}
                    classNames={[styles.inputContainer]}
                    inputClassNames={[styles.inputWrapper]}
                  />
                  <Button
                    text="Apply"
                    onClick={() =>
                      handleApplyChannelValue('email', 'emailAddress', draftEmailAddress)
                    }
                    disabled={isEmailApplyDisabled}
                    backgroundColor="#FFFFFF"
                    borderColor="#E4E4E7"
                    textColor="#A1A1AA"
                    classNames={[styles.applyButton]}
                  />
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.matrix}>
                  <div className={styles.matrixHeading}>CATEGORY</div>

                  {NOTIFICATION_CHANNELS.map((channel) => {
                    const Icon = channelIconByKey[channel.key];
                    return (
                      <div
                        key={channel.key}
                        className={styles.matrixIconCell}
                        title={channel.label}
                      >
                        <Icon className={styles.matrixIcon} />
                      </div>
                    );
                  })}

                  {NOTIFICATION_CATEGORIES.map((category) => (
                    <React.Fragment key={category.key}>
                      <div className={styles.categoryLabel}>{category.label}</div>

                      {NOTIFICATION_CHANNELS.map((channel) => (
                        <div
                          key={`${category.key}-${channel.key}`}
                          className={styles.matrixSwitchCell}
                        >
                          <Switch
                            checked={resolvedPreferences.categories[category.key][channel.key]}
                            onClick={() => handleMatrixToggle(category.key, channel.key)}
                            ariaLabel={`${category.label} ${channel.label}`}
                            disabled={isDisabled}
                          />
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.soundRow}>
                  <span className={styles.soundLabel}>
                    <Volume2 className={styles.soundIcon} />
                    Sound
                  </span>

                  <Switch
                    checked={resolvedPreferences.sound}
                    onClick={handleToggleSound}
                    ariaLabel="Toggle sound"
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className={styles.feed}>{renderTaskNotificationsFeed()}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

Switch.propTypes = {
  checked: PropTypes.bool,
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
};

TaskNotifications.propTypes = {
  preferences: PropTypes.shape({
    notificationsEnabled: PropTypes.bool,
    sound: PropTypes.bool,
    channels: PropTypes.shape({
      browser: PropTypes.object,
      inApp: PropTypes.object,
      slack: PropTypes.shape({
        slackUserId: PropTypes.string,
        slackLabel: PropTypes.string,
      }),
      email: PropTypes.object,
    }),
    categories: PropTypes.shape({
      critical: PropTypes.object,
      assignment: PropTypes.object,
      escalation: PropTypes.object,
      followedChange: PropTypes.object,
      overdue: PropTypes.object,
    }),
  }),
  currentUserEmail: PropTypes.string,
  isLoading: PropTypes.bool,
  isTaskNotificationsLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  onPatchPreferences: PropTypes.func,
  onSearchSlackUsers: PropTypes.func,
  onSendTestMessage: PropTypes.func,
  taskNotifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      clientId: PropTypes.number,
      patientId: PropTypes.number,
      taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      category: PropTypes.number,
      status: PropTypes.number,
      subject: PropTypes.string,
      body: PropTypes.string,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      updatedBy: PropTypes.number,
    }),
  ),
  onDismissTaskNotification: PropTypes.func,
  onOpenTaskNotification: PropTypes.func,
  browserNotificationPermission: PropTypes.string,
  onRequestBrowserNotificationPermission: PropTypes.func,
};

export default TaskNotifications;
